/**
 * Unified Axios Instance for FixIt API
 * Works on both React Native (mobile) and Next.js (web)
 */

import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponseHeaders,
  type InternalAxiosRequestConfig,
  type RawAxiosResponseHeaders,
} from 'axios';

// ===========================================
// Types
// ===========================================

/**
 * Raw API response - opt-in wrapper for when you need HTTP metadata.
 * Use `rawInstance` instead of `customInstance` to get this shape.
 */
export interface RawApiResponse<T> {
  data: T;
  status: number;
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
}

export interface ApiErrorPayload {
  statusCode: number;
  message: string;
  error: string;
  code: string;
  details?: Record<string, unknown> | string[];
  name?: string;
}

export type ErrorType<Error> = Error & { name: string; message: string };

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

export interface TokenStorage {
  /** Identity epoch is independent from access/refresh-token rotation. */
  getSessionEpoch?: () => number;
  getValidAccessToken?: () => Promise<string | null>;
  getAccessToken: () => string | null | Promise<string | null>;
  getRefreshToken: () => string | null | Promise<string | null>;
  setTokens: (tokens: TokenPair) => void | Promise<void>;
  clearTokens: () => void | Promise<void>;
  /**
   * Optional host-owned refresh implementation.
   *
   * Mobile uses this to route axios 401 refreshes through the same
   * single-flight token manager as AppState and WebSocket refreshes. The
   * function must persist the returned tokens before resolving.
   */
  refreshTokens?: () => Promise<TokenPair>;
  /**
   * Called when the server DEFINITIVELY rejected the refresh token (401/403
   * from `/auth/refresh`) - the app should force-logout the user. Never called
   * for transient failures (network, timeout, 429, 5xx); those keep the
   * session and surface as ordinary request errors.
   *
   * `reason` carries the server's stable error code (`auth.token.reused`,
   * `auth.token.expired`, `auth.token.invalid`, …) when present, else
   * `http_<status>` - feed it to observability so production logout causes
   * stay attributable.
   */
  onAuthFailure?: (reason?: string) => void;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Explicit public/captured-credential calls (logout) bypass token attachment. */
    fixitAuth?: 'none';
    /**
     * A write with its own offline queue (the mobile chat send). The mobile
     * app's offline write guard lets it through; nothing in this package
     * reads it.
     */
    fixitOffline?: 'queued';
    _sessionEpoch?: number;
    _retry?: boolean;
    _activeCompanyId?: string | null;
  }
}

function assertRequestSession(config: AxiosRequestConfig | undefined): void {
  if (
    config?._sessionEpoch !== undefined &&
    config._sessionEpoch !== getTokenStorage()?.getSessionEpoch?.()
  ) {
    throw new axios.CanceledError('Session changed during request');
  }
}

// ===========================================
// Active Company Resolver (injectable)
// ===========================================

/**
 * Resolver that returns the current active company id (or `null` for
 * personal context). Injected by the host app at startup so `axios-instance`
 * stays free of any direct dependency on `@fixit/shared` (which would
 * introduce a circular workspace dependency).
 */
export type ActiveCompanyResolver = () => string | null;

/**
 * Store the resolver on `globalThis` rather than as a module-scoped variable.
 *
 * Reason: Metro's monorepo resolver can create duplicate module instances of
 * this file when it's imported via two different specifiers (e.g. the root
 * `@fixit/api-client` package path from `api-client-init` vs the relative
 * `../../axios-instance` path from generated Orval files). Each instance
 * gets its own copy of a module-level `let`, so `setActiveCompanyResolver`
 * on one instance never reaches the interceptor on the other - the header
 * silently fails to attach for the iOS app while working for curl.
 *
 * `globalThis` is shared across all module instances in the JS runtime, so
 * both sides observe the same resolver reference.
 */
const RESOLVER_KEY = '__FIXIT_ACTIVE_COMPANY_RESOLVER__';

type ResolverGlobal = { [RESOLVER_KEY]?: ActiveCompanyResolver | null };

function getResolver(): ActiveCompanyResolver | null {
  return (globalThis as ResolverGlobal)[RESOLVER_KEY] ?? null;
}

/**
 * Register a resolver for the active company id. The interceptor calls this
 * synchronously on every outgoing request and attaches the value as the
 * `X-Active-Company-Id` header when non-null.
 *
 * @example
 * import { useProfileStore, selectActiveCompanyId } from '@fixit/shared/stores/profile.store';
 * setActiveCompanyResolver(() => selectActiveCompanyId(useProfileStore.getState()));
 */
export function setActiveCompanyResolver(resolver: ActiveCompanyResolver | null): void {
  (globalThis as ResolverGlobal)[RESOLVER_KEY] = resolver;
}

const ACTIVE_COMPANY_HEADER = 'X-Active-Company-Id';
const DEVICE_ID_HEADER = 'X-Device-Id';
const DEVICE_TOKEN_HEADER = 'X-Device-Token';
const CURRENT_REFRESH_TOKEN_HEADER = 'X-Current-Refresh-Token';
const ACCEPT_LANGUAGE_HEADER = 'Accept-Language';
const USER_AGENT_HEADER = 'User-Agent';
const TRACE_ID_HEADER = 'X-Trace-Id';

// ===========================================
// Locale Resolver (injectable)
// ===========================================
//
// Returns the current UI locale (e.g. 'cs' or 'en'). Mobile registers a
// resolver reading `i18n.language`; web/admin-web rely on the browser's
// native Accept-Language header so they can omit registration.
//
// Stored on `globalThis` for the same reason as the active-company resolver:
// Metro can duplicate this module across import specifiers, and a module-
// scoped `let` would not be shared between instances.

export type LocaleResolver = () => string | null;

const LOCALE_KEY = '__FIXIT_LOCALE_RESOLVER__';

type LocaleGlobal = { [LOCALE_KEY]?: LocaleResolver | null };

function getLocaleResolver(): LocaleResolver | null {
  return (globalThis as LocaleGlobal)[LOCALE_KEY] ?? null;
}

export function setLocaleResolver(resolver: LocaleResolver | null): void {
  (globalThis as LocaleGlobal)[LOCALE_KEY] = resolver;
}

// ===========================================
// Device ID Resolver (injectable)
// ===========================================

/**
 * Resolver that returns a stable per-install device id. Attached to every
 * request as `X-Device-Id` so the API can group refresh tokens per device.
 * Injected by the host app at startup; lives on `globalThis` for the same
 * reason as the active-company resolver (Metro may duplicate this module
 * across import specifiers).
 */
export type DeviceIdResolver = () => string | null;

const DEVICE_ID_KEY = '__FIXIT_DEVICE_ID_RESOLVER__';

type DeviceIdGlobal = { [DEVICE_ID_KEY]?: DeviceIdResolver | null };

function getDeviceIdResolver(): DeviceIdResolver | null {
  return (globalThis as DeviceIdGlobal)[DEVICE_ID_KEY] ?? null;
}

export function setDeviceIdResolver(resolver: DeviceIdResolver | null): void {
  (globalThis as DeviceIdGlobal)[DEVICE_ID_KEY] = resolver;
}

// ===========================================
// Device Token Resolver (injectable)
// ===========================================

/**
 * Resolver that returns the server-minted signature over the device id
 * (`POST /auth/device-token`), attached as `X-Device-Token`. Anonymous
 * engagement writes (view pings) require it; everything else ignores it.
 * Null until the app has minted one - the host caches it alongside the
 * device id.
 */
export type DeviceTokenResolver = () => string | null;

const DEVICE_TOKEN_KEY = '__FIXIT_DEVICE_TOKEN_RESOLVER__';

type DeviceTokenGlobal = { [DEVICE_TOKEN_KEY]?: DeviceTokenResolver | null };

function getDeviceTokenResolver(): DeviceTokenResolver | null {
  return (globalThis as DeviceTokenGlobal)[DEVICE_TOKEN_KEY] ?? null;
}

export function setDeviceTokenResolver(resolver: DeviceTokenResolver | null): void {
  (globalThis as DeviceTokenGlobal)[DEVICE_TOKEN_KEY] = resolver;
}

// ===========================================
// User-Agent Resolver (injectable, mobile-only)
// ===========================================
//
// React Native's networking layer ships UAs the API can't classify
// (`okhttp/x.y.z` on Android, `<bundle>/<v> CFNetwork/x.y Darwin/x.y` on iOS),
// so every refresh-token row lands as "Unknown device" in the sessions UI.
// Mobile registers a resolver returning a structured UA we control -
// `FixIt/<v> (<model>; <os> <ver>)` - that the API's `DeviceLabelerService`
// parses into `{ kind, label }`.
//
// Web doesn't register a resolver: browsers forbid scripts from setting
// `User-Agent`, and the BFF passthrough already forwards the browser UA.

export type UserAgentResolver = () => string | null;

const USER_AGENT_KEY = '__FIXIT_USER_AGENT_RESOLVER__';

type UserAgentGlobal = { [USER_AGENT_KEY]?: UserAgentResolver | null };

function getUserAgentResolver(): UserAgentResolver | null {
  return (globalThis as UserAgentGlobal)[USER_AGENT_KEY] ?? null;
}

export function setUserAgentResolver(resolver: UserAgentResolver | null): void {
  (globalThis as UserAgentGlobal)[USER_AGENT_KEY] = resolver;
}

// ===========================================
// Trace ID Resolver (injectable)
// ===========================================
//
// Returns a per-inquiry / per-chat-session correlation id. Attached to every
// request as `X-Trace-Id` so the API can stitch the ~5 calls of one inquiry
// flow (describe → /ai/chat → submit → /dispatch/start) onto a single id -
// covering the body-carried calls whose inquiry UUID never reaches the server
// logs otherwise. The API honours it in `genReqId` (lands as `reqId`) and
// stamps the structured `traceId` on every line via its CLS mixin.
//
// Mobile registers a resolver reading the inquiry-create store's per-flow id;
// web doesn't (the inquiry create flow is mobile-only), so the server mints its
// own request id when the header is absent. Stored on `globalThis` for the same
// reason as the active-company resolver (Metro module duplication).

export type TraceIdResolver = () => string | null;

const TRACE_ID_KEY = '__FIXIT_TRACE_ID_RESOLVER__';

type TraceIdGlobal = { [TRACE_ID_KEY]?: TraceIdResolver | null };

function getTraceIdResolver(): TraceIdResolver | null {
  return (globalThis as TraceIdGlobal)[TRACE_ID_KEY] ?? null;
}

export function setTraceIdResolver(resolver: TraceIdResolver | null): void {
  (globalThis as TraceIdGlobal)[TRACE_ID_KEY] = resolver;
}

// ===========================================
// Update-required handler (injectable)
// ===========================================
//
// The API answers HTTP 426 `app.update_required` when the calling mobile app
// is below the support floor (`platform_settings.min_supported_app_version_*`).
// Mobile registers a handler that flips the app-support store so the blocking
// update screen renders; web never sees a 426 (only a `FixIt/<version>` agent
// is gated). The handler receives the FLAT `ApiErrorPayload` the server sent
// (same shape `customInstance` re-throws); the request still rejects afterwards
// so callers see the failure. Stored on `globalThis` for the same reason as
// the resolvers above (Metro module duplication).

export type UpdateRequiredHandler = (payload: ApiErrorPayload) => void;

const UPDATE_REQUIRED_KEY = '__FIXIT_UPDATE_REQUIRED_HANDLER__';

type UpdateRequiredGlobal = { [UPDATE_REQUIRED_KEY]?: UpdateRequiredHandler | null };

function getUpdateRequiredHandler(): UpdateRequiredHandler | null {
  return (globalThis as UpdateRequiredGlobal)[UPDATE_REQUIRED_KEY] ?? null;
}

export function setUpdateRequiredHandler(handler: UpdateRequiredHandler | null): void {
  (globalThis as UpdateRequiredGlobal)[UPDATE_REQUIRED_KEY] = handler;
}

// ===========================================
// Token Storage (injectable for different platforms)
// ===========================================

const TOKEN_STORAGE_KEY = '__FIXIT_TOKEN_STORAGE__';
type TokenStorageGlobal = { [TOKEN_STORAGE_KEY]?: TokenStorage };
function getTokenStorage(): TokenStorage | null {
  return (globalThis as TokenStorageGlobal)[TOKEN_STORAGE_KEY] ?? null;
}

/**
 * Initialize the token storage for your platform
 * Call this before making API requests
 *
 * @example
 * // React Native (MMKV)
 * initializeTokenStorage({
 *   getAccessToken: () => storage.getString('accessToken') ?? null,
 *   getRefreshToken: () => storage.getString('refreshToken') ?? null,
 *   setTokens: (tokens) => {
 *     storage.set('accessToken', tokens.accessToken);
 *     storage.set('refreshToken', tokens.refreshToken);
 *   },
 *   clearTokens: () => {
 *     storage.delete('accessToken');
 *     storage.delete('refreshToken');
 *   },
 * });
 *
 * @example
 * // Next.js (cookies)
 * initializeTokenStorage({
 *   getAccessToken: () => Cookies.get('accessToken') ?? null,
 *   getRefreshToken: () => Cookies.get('refreshToken') ?? null,
 *   setTokens: (tokens) => {
 *     Cookies.set('accessToken', tokens.accessToken);
 *     Cookies.set('refreshToken', tokens.refreshToken);
 *   },
 *   clearTokens: () => {
 *     Cookies.remove('accessToken');
 *     Cookies.remove('refreshToken');
 *   },
 * });
 */
export function initializeTokenStorage(storage: TokenStorage): void {
  (globalThis as TokenStorageGlobal)[TOKEN_STORAGE_KEY] = storage;
}

// ===========================================
// Base URL Detection
// ===========================================

/**
 * NO DEFAULT ON PURPOSE (the rule of apps/client/lib/api/config.ts). This used
 * to fall back to 'http://localhost:3000': a host missing its env var shipped
 * silently and dialed a dev port, and the literal sat in every production iOS
 * bundle, where the release IPA verifier refuses `http://localhost:3xxx`
 * (apps/client/scripts/build-ios-production.ts). Unset now yields an empty,
 * relative base: the misconfiguration fails at its first request (Node refuses
 * a relative URL, a browser asks its own origin) and the warning below names
 * the variables. Hosts that pin the base themselves (apps/bozp sets
 * `axiosInstance.defaults.baseURL`) are unaffected.
 */
function getBaseUrl(): string {
  // Next.js environment variable
  if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  // Expo environment variable
  if (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_API_URL) {
    return process.env.EXPO_PUBLIC_API_URL;
  }
  return '';
}

// ===========================================
// Axios Instance
// ===========================================

const baseUrl = getBaseUrl();
if (baseUrl) {
  console.log(`[FixIt API] Using base URL: ${baseUrl}`);
} else {
  console.warn(
    '[FixIt API] No base URL: set NEXT_PUBLIC_API_URL (web) or EXPO_PUBLIC_API_URL (app). ' +
      'Requests resolve relative to the current origin until the host sets one.',
  );
}

const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // Serialize arrays as key=v1&key=v2 (NestJS format), not key[]=v1&key[]=v2
  },
});

// ===========================================
// Request Interceptor - Attach Token
// ===========================================

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const tokenStorage = getTokenStorage();
    if (config._sessionEpoch === undefined)
      config._sessionEpoch = tokenStorage?.getSessionEpoch?.();
    assertRequestSession(config);
    if (tokenStorage && config.fixitAuth !== 'none') {
      // Auth exchanges must never wait for the credential they are creating.
      const token =
        !isAuthEndpoint(config.url) && tokenStorage.getValidAccessToken
          ? await tokenStorage.getValidAccessToken()
          : await tokenStorage.getAccessToken();
      assertRequestSession(config);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (config._retry) {
        delete config.headers.Authorization;
      }
    }

    // Attach (or strip) the active company header. Called synchronously so the
    // store read never blocks the request pipeline. Reads via `globalThis`
    // because Metro can duplicate this module under different import paths
    // and a module-scoped `let` would not be shared.
    if (config.headers) {
      // A caller-supplied header wins over the resolver - cross-profile
      // PREFETCH (warming another company's cache from the profile switcher)
      // must be able to target a company that is not the active one.
      if (config.headers[ACTIVE_COMPANY_HEADER] == null) {
        const activeCompanyId =
          config._activeCompanyId === undefined
            ? (getResolver()?.() ?? null)
            : config._activeCompanyId;
        config._activeCompanyId = activeCompanyId;
        if (activeCompanyId) {
          config.headers[ACTIVE_COMPANY_HEADER] = activeCompanyId;
        } else {
          // Defensive: clear any stale header if the interceptor is reinvoked
          // on a retried request after the user switched back to personal.
          delete config.headers[ACTIVE_COMPANY_HEADER];
        }
      }

      // X-Device-Id - attached to every authenticated request so the API
      // can group refresh tokens per physical device in the session UI.
      const deviceId = getDeviceIdResolver()?.() ?? null;
      if (deviceId) {
        config.headers[DEVICE_ID_HEADER] = deviceId;
      }

      // X-Device-Token - the server-minted signature over X-Device-Id.
      // Required by anonymous engagement writes (view pings); harmless noise
      // for every other endpoint.
      const deviceToken = getDeviceTokenResolver()?.() ?? null;
      if (deviceToken) {
        config.headers[DEVICE_TOKEN_HEADER] = deviceToken;
      }

      // User-Agent - mobile-only. Replaces the platform-default UA
      // (`okhttp/...` / `CFNetwork/...`) the API otherwise can't classify,
      // so refresh-token rows label as the real device model on the
      // sessions screen instead of "Unknown device".
      const userAgent = getUserAgentResolver()?.() ?? null;
      if (userAgent) {
        config.headers[USER_AGENT_HEADER] = userAgent;
      }

      // Accept-Language - attached on every request so the server picks
      // localized strings (skill names, category names, marketplace copy,
      // notification templates) consistent with the UI's i18n state.
      // Mobile registers a resolver reading `i18n.language`; web defers
      // to the browser's native header.
      const locale = getLocaleResolver()?.() ?? null;
      if (locale) {
        config.headers[ACCEPT_LANGUAGE_HEADER] = locale;
      }

      // X-Trace-Id - per-inquiry / per-chat-session correlation id so the
      // server can stitch one inquiry flow's calls (incl. body-carried
      // /ai/chat + /dispatch/start) onto a single id in the logs + audit.
      // Null outside the create flow → server mints its own request id.
      const traceId = getTraceIdResolver()?.() ?? null;
      if (traceId) {
        config.headers[TRACE_ID_HEADER] = traceId;
      } else {
        // Defensive: drop any stale id if the interceptor is reinvoked on a
        // retried request after the flow ended / reset.
        delete config.headers[TRACE_ID_HEADER];
      }

      // X-Current-Refresh-Token - only on the session-management routes,
      // so the server can mark which row corresponds to the caller's
      // current device in the list + `revoke-others` responses.
      if (tokenStorage && config.url?.includes('/auth/sessions')) {
        const currentRefresh = await tokenStorage.getRefreshToken();
        if (currentRefresh) {
          config.headers[CURRENT_REFRESH_TOKEN_HEADER] = currentRefresh;
        }
      }
    }

    assertRequestSession(config);
    return config;
  },
  (error) => Promise.reject(error),
);

// ===========================================
// Response Interceptor - Handle 401 & Refresh
// ===========================================

let refreshFlight: {
  storage: TokenStorage;
  epoch: number | undefined;
  promise: Promise<TokenPair>;
} | null = null;

/**
 * HTTP status of a failed request, whatever error shape reached the caller.
 *
 * Errors from generated endpoints pass through `customInstance`, which
 * re-throws the FLAT `ApiErrorPayload` (`statusCode`, no `.response`) - while
 * raw axios calls (and the interceptor's internal refresh) reject with an
 * `AxiosError` (`.response.status`, no `.statusCode`). Classifiers that read
 * only one shape silently never match the other; that exact trap broke the
 * join-by-code error copy (see `classifyLookupError`) and the mobile
 * definitive-401 logout leg. Always classify through this helper.
 */
export function apiErrorStatus(error: unknown): number | undefined {
  const axiosStatus = (error as AxiosError | undefined)?.response?.status;
  if (typeof axiosStatus === 'number') return axiosStatus;
  const directStatus = (error as { status?: unknown } | undefined)?.status;
  if (typeof directStatus === 'number') return directStatus;
  const payloadStatus = (error as { statusCode?: unknown } | undefined)?.statusCode;
  return typeof payloadStatus === 'number' ? payloadStatus : undefined;
}

/**
 * The API's typed error code (`ApiErrorCode`), whatever error shape reached
 * the caller - the same two-shape trap as {@link apiErrorStatus}.
 *
 * Branch on THIS, never on the status alone: one endpoint answers 403 for
 * several different reasons (the booth's submit refuses an unverified phone
 * AND a nameless applicant), and the message is user-facing copy that may be
 * translated or reworded at any time.
 */
export function apiErrorCode(error: unknown): string | undefined {
  const payloadCode = (error as { code?: unknown } | undefined)?.code;
  if (typeof payloadCode === 'string') return payloadCode;
  const axiosCode = (error as AxiosError<ApiErrorPayload> | undefined)?.response?.data?.code;
  return typeof axiosCode === 'string' ? axiosCode : undefined;
}

/**
 * A refresh failure only proves the session is dead when the SERVER said so:
 * a 401/403 response from the refresh endpoint. Everything else - network
 * drop, timeout, 429 (shared-IP throttle), 5xx, proxy garbage - is transient:
 * the refresh token is still valid server-side, and destroying it client-side
 * turns a bad radio moment into a permanent logout (the 2026-08 random-logout
 * incident, mechanism #1).
 *
 * Wire reality (probed 2026-08-05): the refresh endpoint's 401 body is a full
 * `ApiErrorPayload`, so `customInstance` re-throws the FLAT payload - the
 * definitive signal arrives as `statusCode`, never `.response.status`.
 */
export function isDefinitiveAuthRejection(error: unknown): boolean {
  const status = apiErrorStatus(error);
  return status === 401 || status === 403;
}

/** Stable reason string for observability: server error code, else HTTP status. */
export function refreshFailureReason(error: unknown): string {
  const payloadCode = (error as { code?: unknown } | undefined)?.code;
  if (
    typeof payloadCode === 'string' &&
    payloadCode.length > 0 &&
    payloadCode !== 'NETWORK_ERROR'
  ) {
    return payloadCode;
  }
  const responseCode = (
    (error as AxiosError | undefined)?.response?.data as { code?: unknown } | undefined
  )?.code;
  if (typeof responseCode === 'string' && responseCode.length > 0) return responseCode;
  const status = apiErrorStatus(error);
  return status !== undefined && status !== 0 ? `http_${status}` : 'no_response';
}

/**
 * Bounded retry for TRANSIENT refresh failures. Server-side rotation is
 * idempotent (replay window), so re-sending the same refresh token after a
 * timeout is safe - the server returns the same successor pair. Definitive
 * 401/403 rejections are never retried.
 *
 * Exported so host token managers (mobile) share the exact same backoff
 * policy instead of hand-rolling their own copy.
 */
export async function refreshWithRetry<T>(doRefresh: () => Promise<T>): Promise<T> {
  const backoffMs = [1000, 3000];
  for (let attempt = 0; ; attempt++) {
    try {
      return await doRefresh();
    } catch (error) {
      if (
        isDefinitiveAuthRejection(error) ||
        axios.isCancel(error) ||
        (error instanceof Error && 'code' in error && error.code === 'SESSION_CHANGED') ||
        attempt >= backoffMs.length
      )
        throw error;
      await new Promise<void>((resolve) => setTimeout(() => resolve(), backoffMs[attempt]));
    }
  }
}

/**
 * Check if the request URL is an auth endpoint that shouldn't trigger token refresh
 * Auth endpoints (login, register, etc.) are supposed to CREATE tokens, not require them
 *
 * Exported for the mobile offline write guard, which never refuses a token exchange.
 */
export function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  // Strip query params and fragments before matching
  const pathname = url.split('?')[0].split('#')[0];
  const authPaths = [
    '/auth/magic-link',
    '/auth/dev-login',
    '/auth/google',
    '/auth/apple',
    '/auth/refresh',
    '/auth/register',
    '/auth/login',
    '/auth/passkey/auth',
    '/auth/passkey/recovery/use',
    '/auth/phone/',
    '/auth/methods',
  ];
  return authPaths.some((path) => pathname.includes(path));
}

axiosInstance.interceptors.response.use(
  (response) => {
    assertRequestSession(response.config);
    return response;
  },
  async (error: AxiosError) => {
    const tokenStorage = getTokenStorage();
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    assertRequestSession(originalRequest);

    // 426 Upgrade Required: the app is below the support floor. Tell the host
    // FIRST (before any refresh logic) so a dead build with an expired session
    // shows the update screen instead of a login loop, then reject as usual.
    if (error.response?.status === 426) {
      const handler = getUpdateRequiredHandler();
      const payload = error.response.data;
      if (handler && isApiErrorPayload(payload)) handler(payload);
      return Promise.reject(error);
    }

    // Skip refresh logic for auth endpoints - they create tokens, not require them
    if (isAuthEndpoint(originalRequest?.url)) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      originalRequest.fixitAuth !== 'none' &&
      tokenStorage
    ) {
      const storage = tokenStorage;
      const epoch = originalRequest._sessionEpoch;
      const currentRefreshToken = await storage.getRefreshToken();
      assertRequestSession(originalRequest);
      if (!currentRefreshToken) return Promise.reject(error);

      // Every participant spends its one retry BEFORE joining the flight.
      originalRequest._retry = true;
      const failedAuthorization = originalRequest.headers.Authorization;
      const currentAccessToken = await storage.getAccessToken();
      assertRequestSession(originalRequest);
      if (currentAccessToken && failedAuthorization !== `Bearer ${currentAccessToken}`) {
        return axiosInstance(originalRequest);
      }

      if (!refreshFlight || refreshFlight.storage !== storage || refreshFlight.epoch !== epoch) {
        const promise = (async (): Promise<TokenPair> => {
          try {
            if (storage.refreshTokens) return await storage.refreshTokens();
            const newTokens = await refreshWithRetry(async () => {
              assertRequestSession(originalRequest);
              const response = await axios.post<TokenPair>(
                `${getBaseUrl()}/api/v1/auth/refresh`,
                { refreshToken: currentRefreshToken },
                { headers: { 'Content-Type': 'application/json' } },
              );
              return response.data;
            });
            assertRequestSession(originalRequest);
            await storage.setTokens(newTokens);
            assertRequestSession(originalRequest);
            return newTokens;
          } catch (refreshError) {
            assertRequestSession(originalRequest);
            const latestRefreshToken = await storage.getRefreshToken();
            const latestAccessToken = await storage.getAccessToken();
            assertRequestSession(originalRequest);
            if (
              latestRefreshToken &&
              latestRefreshToken !== currentRefreshToken &&
              latestAccessToken
            ) {
              return { accessToken: latestAccessToken, refreshToken: latestRefreshToken };
            }
            if (isDefinitiveAuthRejection(refreshError)) {
              // Notify synchronously while this epoch is still owned. A delayed
              // secure delete must never force-logout a subsequent login.
              const clearing = storage.clearTokens();
              try {
                storage.onAuthFailure?.(refreshFailureReason(refreshError));
              } catch (handlerError) {
                console.error('[Auth] failure handler failed:', handlerError);
              }
              try {
                await clearing;
              } catch (clearError) {
                console.error('[Auth] clearTokens failed:', clearError);
                throw clearError;
              }
            }
            throw refreshError;
          }
        })();
        const flight = { storage, epoch, promise };
        refreshFlight = flight;
        void promise.then(
          () => {
            if (refreshFlight === flight) refreshFlight = null;
          },
          () => {
            if (refreshFlight === flight) refreshFlight = null;
          },
        );
      }
      await refreshFlight.promise;
      assertRequestSession(originalRequest);
      return axiosInstance(originalRequest);
    }

    return Promise.reject(error);
  },
);

// ===========================================
// Response Utilities
// ===========================================

function isApiErrorPayload(value: unknown): value is ApiErrorPayload {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Record<string, unknown>;
  return (
    typeof payload.statusCode === 'number' &&
    typeof payload.message === 'string' &&
    typeof payload.error === 'string' &&
    typeof payload.code === 'string'
  );
}

/**
 * `Retry-After` in seconds (delta-seconds or an HTTP-date), or undefined.
 * Exported for tests.
 */
export function parseRetryAfterHeader(raw: unknown, now: number = Date.now()): number | undefined {
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  return Number.isNaN(at) ? undefined : Math.max(0, Math.ceil((at - now) / 1000));
}

function toFallbackErrorPayload(error: AxiosError): ApiErrorPayload {
  // Partial API bodies (e.g. Nest's `ConflictException({ code, message,
  // details })` - no statusCode/error fields) are still the server talking:
  // keep their fields so callers can read `code` / `details` etc. The
  // transport-derived statusCode always wins over anything in the body.
  const body = error.response?.data;
  const bodyFields =
    body && typeof body === 'object' && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : {};
  // A proxy's 429 (nginx's per-IP / per-token backstops) carries no API body,
  // only a `Retry-After` header. Fold it into `details.retryAfter` - the field
  // the API's own 429 uses and `readRetryAfterSeconds` reads - unless the body
  // already says.
  const retryAfter = parseRetryAfterHeader(error.response?.headers?.['retry-after']);
  const bodyDetails = bodyFields.details;
  const detailRecord =
    bodyDetails && typeof bodyDetails === 'object' && !Array.isArray(bodyDetails)
      ? (bodyDetails as Record<string, unknown>)
      : undefined;
  const foldRetryAfter =
    retryAfter !== undefined &&
    (bodyDetails === undefined || (detailRecord !== undefined && !('retryAfter' in detailRecord)));
  return {
    name: 'NetworkError',
    message: error.message || 'Network request failed',
    error: error.response?.statusText || 'Request Failed',
    code: 'NETWORK_ERROR',
    ...bodyFields,
    ...(foldRetryAfter ? { details: { ...detailRecord, retryAfter } } : {}),
    statusCode: error.response?.status ?? 0,
  };
}

// ===========================================
// Orval Custom Instance (Mutator)
// ===========================================

/**
 * Wire-shape gate: a JSON endpoint whose body fails to parse leaves
 * `response.data` as a RAW STRING (axios's default transform swallows the
 * JSON.parse failure). Proven live 2026-07-24: a localhost→::1 hijack fed the
 * app HTML/garbage with HTTP 200; the string flowed into stores and crashed
 * near-root providers (`"…".filter is not a function`) or silently wiped
 * auth state. Any captive portal / proxy / 200-error-page reproduces it.
 * Reject it at the boundary so it behaves like a 5xx and lands in the
 * already-handled error paths. Requests that legitimately expect text/blob
 * opt out via `config.responseType`.
 */
const assertJsonResponseShape = (config: AxiosRequestConfig, data: unknown): void => {
  const expectsJson = !config.responseType || config.responseType === 'json';
  if (expectsJson && typeof data === 'string' && data.length > 0) {
    throw {
      name: 'InvalidResponseError',
      statusCode: 0,
      message: 'Non-JSON body on a JSON endpoint (proxy/captive portal or hijacked route?)',
      error: 'Invalid Response',
      code: 'INVALID_RESPONSE',
    } satisfies ApiErrorPayload;
  }
};

/** Options `withRequestOptions` hands to the next `customInstance` call. */
let scopedRequestOptions: AxiosRequestConfig | null = null;

/**
 * Call a generated function with per-request options it has no parameter for.
 * Orval's functions take only params/body/signal, so an explicit credential
 * (sign-out after the session has advanced) or a pinned `X-Active-Company-Id`
 * used to force a hand-built `customInstance({ url })` - an untyped route that
 * a renamed endpoint turns into a runtime 404 - or was silently dropped as an
 * extra argument. A generated function reaches `customInstance` synchronously,
 * so the options apply to exactly that one request; headers are merged, the
 * caller's win.
 *
 * @example
 * withRequestOptions({ headers: { 'X-Active-Company-Id': companyId } }, () =>
 *   employeesControllerGetCurrentEmployeeV1(),
 * );
 */
export function withRequestOptions<T>(
  options: AxiosRequestConfig,
  call: () => Promise<T>,
): Promise<T> {
  const previous = scopedRequestOptions;
  scopedRequestOptions = options;
  try {
    const pending = call();
    if (scopedRequestOptions === options) {
      throw new Error('withRequestOptions: the call did not issue a request synchronously');
    }
    return pending;
  } finally {
    scopedRequestOptions = previous;
  }
}

/**
 * Default Orval mutator - returns the response DTO directly.
 * Axios throws on non-2xx, so success is implicit.
 * Use try/catch or React Query onError for error handling.
 */
export const customInstance = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const scoped = scopedRequestOptions;
  scopedRequestOptions = null;
  config = {
    ...config,
    ...scoped,
    headers: { ...config.headers, ...scoped?.headers },
    _sessionEpoch: config._sessionEpoch ?? getTokenStorage()?.getSessionEpoch?.(),
  };
  try {
    const response = await axiosInstance(config);
    assertJsonResponseShape(config, response.data);
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    if (axios.isAxiosError(error)) {
      if (isApiErrorPayload(error.response?.data)) {
        throw {
          ...error.response.data,
          name: (error.response.data as ApiErrorPayload).name ?? 'ApiError',
        } satisfies ApiErrorPayload;
      }
      throw toFallbackErrorPayload(error);
    }
    throw error;
  }
};

/**
 * Opt-in raw instance - returns HTTP metadata alongside the DTO.
 * Use when you need response headers or the HTTP status code.
 */
export const rawInstance = async <T>(config: AxiosRequestConfig): Promise<RawApiResponse<T>> => {
  config = {
    ...config,
    _sessionEpoch: config._sessionEpoch ?? getTokenStorage()?.getSessionEpoch?.(),
  };
  try {
    const response = await axiosInstance(config);
    assertJsonResponseShape(config, response.data);
    return { data: response.data, status: response.status, headers: response.headers };
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    if (axios.isAxiosError(error)) {
      if (isApiErrorPayload(error.response?.data)) {
        throw {
          ...error.response.data,
          name: (error.response.data as ApiErrorPayload).name ?? 'ApiError',
        } satisfies ApiErrorPayload;
      }
      throw toFallbackErrorPayload(error);
    }
    throw error;
  }
};

// Export axios instance for direct use if needed
export { axiosInstance };

// Default export for Orval mutator
export default customInstance;
